import React, { useMemo, useState } from "react";
import { useStatsTooltip } from "./StatsTooltip";
import { HEAT_RAMP, buildHeatTable, rampColor } from "./statsChart";
import { hourRange } from "./statsFormat";
import { weekdayLong } from "./statsHeat";

const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * Weekday x hour heatmap panel shared by the court and the Beauty statistics. Each cell is booked /
 * open time of that weekday and hour, averaged over the period, so a darker cell is a busier slot.
 * Hours with nothing open (or blocked) are hatched instead of drawn as 0 %, because "nobody came"
 * and "nobody could come" call for different decisions. "Ver tabla" swaps the map for the same
 * percentages as text.
 *
 * @param {{grid: {hours: number[], rows: Array}, title: string, subtitle: string, ariaLabel: string,
 *   closedLabel: string, closedTip: string,
 *   openTip: (row: object, cell: object) => Array<{value: string, label?: string}>}} props
 *   `grid` comes from a `buildHeatGrid`; the copy is per screen ("canchas" vs "profesionales");
 *   `openTip` returns the tooltip rows of an open cell, `closedTip` is the text of a closed one.
 */
export default function HeatPanel({ grid, title, subtitle, ariaLabel, closedLabel, closedTip, openTip }) {
  const tooltip = useStatsTooltip();
  const [view, setView] = useState("chart");
  const table = useMemo(() => buildHeatTable(grid), [grid]);

  const heading = (row, cell) => `${capitalize(weekdayLong(row.weekday))} de ${hourRange(cell.hour)}`;

  const showOpen = (row, cell, event) => tooltip.show(heading(row, cell), openTip(row, cell), event.clientX, event.clientY);
  const showClosed = (row, cell, event) => tooltip.show(heading(row, cell), [{ value: closedTip }], event.clientX, event.clientY);

  return (
    <section className="stats-panel" aria-labelledby="stats-heat-title">
      <div className="stats-panel__head">
        <div>
          <h2 className="stats-panel__title" id="stats-heat-title">
            {title}
          </h2>
          <p className="stats-panel__sub">{subtitle}</p>
        </div>
        <div className="stats-panel__tools">
          <button
            type="button"
            className="stats-ghost-btn"
            aria-pressed={view === "table"}
            onClick={() => setView((v) => (v === "chart" ? "table" : "chart"))}
          >
            {view === "chart" ? "Ver tabla" : "Ver gráfico"}
          </button>
        </div>
      </div>

      <div className="stats-heat-legend">
        <span className="stats-heat-legend__scale">
          <span>0 %</span>
          <span className="stats-heat-legend__bar" style={{ background: `linear-gradient(90deg, ${HEAT_RAMP.join(", ")})` }} />
          <span>100 %</span>
        </span>
        <span className="stats-heat-legend__scale">
          <span className="stats-closed-swatch" aria-hidden="true" />
          {closedLabel}
        </span>
      </div>

      {view === "chart" ? (
        <div className="stats-heat-scroll">
          <div
            className="stats-heat"
            style={{ gridTemplateColumns: `44px repeat(${grid.hours.length}, minmax(30px, 1fr))` }}
            role="img"
            aria-label={ariaLabel}
          >
            <div />
            {grid.hours.map((hour) => (
              <div key={hour} className="stats-heat__hour">
                {String(hour).padStart(2, "0")}
              </div>
            ))}
            {grid.rows.map((row) => (
              <React.Fragment key={row.weekday}>
                <div className="stats-heat__day">{row.label}</div>
                {row.cells.map((cell) =>
                  cell.value === null ? (
                    <div
                      key={cell.hour}
                      className="stats-heat__cell closed"
                      data-weekday={row.weekday}
                      data-hour={cell.hour}
                      onPointerMove={(event) => showClosed(row, cell, event)}
                      onPointerLeave={tooltip.hide}
                    />
                  ) : (
                    <div
                      key={cell.hour}
                      className="stats-heat__cell"
                      data-weekday={row.weekday}
                      data-hour={cell.hour}
                      style={{ background: rampColor(cell.value) }}
                      onPointerMove={(event) => showOpen(row, cell, event)}
                      onPointerLeave={tooltip.hide}
                    />
                  )
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      ) : (
        <div className="stats-table-wrap">
          <table>
            <thead>
              <tr>
                {table.headers.map((header, index) => (
                  <th key={header} className={index ? "num" : undefined}>
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row) => (
                <tr key={row[0]}>
                  {row.map((cell, index) => (
                    <td key={index} className={index ? "num" : undefined}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
